import { useCallback, useEffect, useState } from 'react'
import { contactOverviewApi, type ContactContract } from '../api/contactOverview'
import { referralsApi, type ReferralList } from '../api/referrals'
import { Button, Callout, Card, CardContent, CardHeader, Dialog, FormGrid, List, ListItem, Select, StatusPill, TextField } from './ui'
import type { LoginResponse } from '../api/auth'
import { ADMIN_ROLES } from '../navigation/roles'

/**
 * "Porta un amico" (Ermes) nella scheda cliente: amici portati, data firma del loro contratto, bonus pagato/totale.
 * "Scarica modulo" scarica il PDF del foglio corrente; il caricamento su Ermes e' manuale (dalla bozza Ermes della pratica
 * dell'amico parte da solo). Senza il modulo vergine un Admin lo carica da qui, una volta sola.
 */
const MANAGER_ROLES = ['Administrator', 'EqualAdmin', 'EqualDirettoreGenerale', 'EqualDirettoreCommerciale', 'EqualAreaManager']

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString('it-IT', { timeZone: 'Europe/Rome' })
}

function euro(n: number): string {
  return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })
}

/**
 * Lo stesso pannello nella scheda cliente (27/10/2026: il foglio si cercava lì e non si trovava): i referral sono per
 * contatto, quindi prima si risolve il contatto collegato al cliente.
 */
export function CustomerReferralPanel({ customerId, auth }: { customerId: number; auth: LoginResponse }) {
  const [contactId, setContactId] = useState<number | null | undefined>(undefined)
  useEffect(() => {
    let alive = true
    referralsApi.contactForCustomer(customerId)
      .then(r => { if (alive) setContactId(r.contactId) })
      .catch(() => { if (alive) setContactId(null) })
    return () => { alive = false }
  }, [customerId])
  if (contactId === undefined) return null
  if (contactId === null) {
    return (
      <Card variant="outlined">
        <CardHeader title="Porta un amico" />
        <CardContent>
          <p className="md-typescale-body-small text-muted m-0">Nessun contatto collegato a questo cliente: gli amici portati si gestiscono dalla scheda del contatto.</p>
        </CardContent>
      </Card>
    )
  }
  return <ReferralPanel contactId={contactId} auth={auth} />
}

export function ReferralPanel({ contactId, auth }: { contactId: number; auth: LoginResponse }) {
  const canPay = MANAGER_ROLES.includes(auth.role)
  const isAdmin = ADMIN_ROLES.includes(auth.role)
  const [data, setData] = useState<ReferralList | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [busy, setBusy] = useState(false)
  const [query, setQuery] = useState('')
  const [found, setFound] = useState<{ id: number; label: string }[]>([])
  const [friendId, setFriendId] = useState<number | null>(null)
  const [contracts, setContracts] = useState<ContactContract[]>([])
  const [contractId, setContractId] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [note, setNote] = useState('')
  const [cids, setCids] = useState<Record<number, string>>({})

  const load = useCallback(async () => {
    try {
      setData(await referralsApi.list(contactId))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossibile caricare gli amici portati')
    }
  }, [contactId])

  useEffect(() => { void load() }, [load])

  async function search() {
    if (query.trim().length < 2) return
    try {
      const res = await referralsApi.searchContacts(query.trim())
      setFound(res.items.filter(c => c.id !== contactId).map(c => ({
        id: c.id,
        label: c.companyName || `${c.firstName} ${c.lastName ?? ''}`.trim(),
      })))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ricerca non riuscita')
    }
  }

  async function pick(id: number, label: string) {
    setFriendId(id)
    setCompanyName(label)
    setContractId('')
    try {
      setContracts((await contactOverviewApi.get(id)).contracts)
    } catch {
      setContracts([])
    }
  }

  function reset() {
    setAdding(false); setQuery(''); setFound([]); setFriendId(null); setContracts([])
    setContractId(''); setCompanyName(''); setNote('')
  }

  async function submit() {
    setBusy(true); setError(null)
    try {
      await referralsApi.create({
        referrerId: contactId,
        friendContactId: friendId ?? undefined,
        friendContractId: contractId ? Number(contractId) : undefined,
        friendCompanyName: companyName.trim() || undefined,
        note: note.trim() || undefined,
      })
      reset()
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossibile aggiungere l\'amico')
    } finally {
      setBusy(false)
    }
  }

  async function pay(id: number) {
    setBusy(true); setError(null)
    try {
      await referralsApi.markPaid(id)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossibile segnare il bonus')
    } finally {
      setBusy(false)
    }
  }

  async function uploadBlank(file: File) {
    setBusy(true); setError(null)
    try {
      await referralsApi.uploadBlankSheet(file)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Caricamento del modulo vergine non riuscito')
    } finally {
      setBusy(false)
    }
  }

  async function uploadErmes(id: number) {
    const raw = (cids[id] ?? '').trim()
    if (!/^\d{1,9}$/.test(raw) || Number(raw) <= 0) { setError('Il numero della bozza Ermes deve essere numerico.'); return }
    setBusy(true); setError(null)
    try {
      await referralsApi.ermesUpload(id, Number(raw))
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Caricamento su Ermes non riuscito')
    } finally {
      setBusy(false)
    }
  }

  if (!data) return error ? <Callout tone="error">{error}</Callout> : null

  return (
    <Card variant="outlined" padding="none">
      <CardHeader
        title={`Porta un amico (${data.items.length})`}
        actions={
          <>
            <StatusPill tone="info">Bonus {euro(data.bonusPaid)} / {euro(data.bonusTotal)}</StatusPill>
            <Button type="button" variant="tonal" size="sm" onClick={() => { setError(null); setAdding(true) }}>Aggiungi amico</Button>
            {data.hasBlankSheet && data.items.length > 0 && (
              <a href={referralsApi.sheetUrl(contactId)} download>
                <Button type="button" variant="outlined" size="sm">Scarica modulo</Button>
              </a>
            )}
          </>
        }
      />
      <CardContent>
        {error && !adding && <Callout tone="error">{error}</Callout>}
        {!data.hasBlankSheet && (
          <p className="md-typescale-body-small text-muted m-0">Modulo vergine non ancora caricato: il download e il caricamento su Ermes non sono disponibili.</p>
        )}
        {!data.hasBlankSheet && isAdmin && (
          <p className="md-typescale-body-small m-0">
            <label>
              Carica il PDF del modulo «Porta un amico» di Ermes (una volta per tutti i clienti):{' '}
              <input
                type="file"
                accept="application/pdf,.pdf"
                disabled={busy}
                onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void uploadBlank(f) }}
              />
            </label>
          </p>
        )}
        <p className="md-typescale-body-small text-muted m-0">Per caricare il modulo su Ermes indica il numero della bozza dell'amico (la pratica deve essere ancora in bozza).</p>
      </CardContent>
      <List aria-label="Amici portati">
        {data.items.length === 0 && <ListItem headline={<span className="text-muted">Nessun amico portato.</span>} />}
        {data.items.map(r => (
          <ListItem
            key={r.id}
            headline={`${r.friendCompanyName} (foglio ${r.sheetNumber}, riga ${r.rowNumber})`}
            supporting={
              `Contratto firmato: ${r.friendContractSignedAt ? formatDay(r.friendContractSignedAt) : 'non indicato'}` +
              (r.friendContractReference ? ` (${r.friendContractReference})` : '') +
              ` · Bonus: ${r.bonusPaidAt ? `pagato ${euro(r.bonusPaidAmount ?? r.bonusAmount)} il ${formatDay(r.bonusPaidAt)}` : `${euro(r.bonusAmount)} da pagare`}` +
              (r.ermesUploadedAt ? ` · Caricato su Ermes il ${formatDay(r.ermesUploadedAt)} (bozza ${r.ermesCid})` : '')
            }
            trailing={
              <>
                {data.hasBlankSheet && (
                  <>
                    <input
                      aria-label={`Numero bozza Ermes di ${r.friendCompanyName}`}
                      inputMode="numeric"
                      placeholder="Bozza Ermes"
                      className="w-28"
                      value={cids[r.id] ?? (r.ermesCid ? String(r.ermesCid) : '')}
                      onChange={e => setCids(c => ({ ...c, [r.id]: e.target.value }))}
                    />
                    <Button type="button" variant="outlined" size="sm" disabled={busy} onClick={() => void uploadErmes(r.id)}>
                      Carica su Ermes
                    </Button>
                  </>
                )}
                {canPay && !r.bonusPaidAt && (
                  <Button type="button" variant="text" size="sm" disabled={busy} onClick={() => void pay(r.id)}>Segna pagato</Button>
                )}
              </>
            }
          />
        ))}
      </List>
      <Dialog
        open={adding}
        onClose={reset}
        title="Aggiungi amico"
        size="md"
        as="form"
        onSubmit={e => { e.preventDefault(); void submit() }}
        actions={
          <>
            <Button type="button" variant="text" onClick={reset}>Annulla</Button>
            <Button type="submit" variant="text" disabled={busy || (friendId === null && !companyName.trim())}>Aggiungi</Button>
          </>
        }
      >
        {error && <Callout tone="error">{error}</Callout>}
        <FormGrid>
          <TextField id="referral-search" label="Cerca cliente amico" value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void search() } }} />
          <Button type="button" variant="outlined" size="sm" onClick={() => void search()}>Cerca</Button>
          {found.length > 0 && (
            <Select id="referral-friend" label="Cliente trovato" value={friendId === null ? '' : String(friendId)}
              onChange={e => {
                const f = found.find(x => String(x.id) === e.target.value)
                if (f) void pick(f.id, f.label)
              }}>
              <option value="">Seleziona...</option>
              {found.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
            </Select>
          )}
          {friendId !== null && (
            <Select id="referral-contract" label="Contratto dell'amico (facoltativo)" value={contractId}
              onChange={e => setContractId(e.target.value)}>
              <option value="">Nessuno</option>
              {contracts.map(k => (
                <option key={k.id} value={k.id}>{k.referenceCode}{k.signedAt ? ` - firmato ${formatDay(k.signedAt)}` : ''}</option>
              ))}
            </Select>
          )}
          <TextField id="referral-company" label="Azienda (ragione sociale)" value={companyName}
            maxLength={255} onChange={e => setCompanyName(e.target.value)} />
          <TextField id="referral-note" label="Nota (facoltativa)" value={note}
            maxLength={1000} onChange={e => setNote(e.target.value)} />
        </FormGrid>
      </Dialog>
    </Card>
  )
}
